import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { collection, query, orderBy, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import ListingItem from '../components/ListingItem';
import Spinner from '../components/Spinner';
import { toast } from 'react-toastify';

export default function CategoryListings() {
  const { category } = useParams();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);

  // Mapping of URL-friendly category names to Firestore category names for digital products
  const categoryMap = {
    'trending': 'Trending',
    'mobile-apps': 'Mobile Apps',
    'web-apps': 'Web Apps',
    'brand-startups': 'Brand Startups',
    'backend-systems': 'Backend Systems',
    'ui-ux-kits': 'UI/UX Kits',
    'ai-automation': 'AI & Automation Tools',
    'programming-courses': 'Programming Courses',
    'graphic-designs': 'Graphic designs',
  };

  // Convert URL parameter to Firestore category name
  const firestoreCategory = categoryMap[category] || category;

  // Format category name for display (capitalize first letter of each word)
  const formatCategoryName = (cat) => {
    return cat.split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Fetch listings by category
  useEffect(() => {
    async function fetchData() {
      try {
        const listingsRef = collection(db, 'listings');
        const listingsQuery = query(listingsRef, orderBy('timestamp', 'desc'));
        const listingsSnap = await getDocs(listingsQuery);
        const listingList = listingsSnap.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        // Log fetched listings for debugging
        console.log('Fetched Listings:', listingList);

        // Filter by category (case-insensitive)
        const filteredListings = listingList.filter(listing => 
          listing.category?.toLowerCase() === firestoreCategory.toLowerCase()
        );

        // Log filtered listings for debugging
        console.log(`Filtered Listings for category "${firestoreCategory}":`, filteredListings);

        setListings(filteredListings);
      } catch (error) {
        toast.error('Failed to load listings');
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [firestoreCategory]);

  if (loading) {
    return <Spinner />;
  }

  return (
    <main className="min-h-screen py-2 px-2 sm:px-4 bg-black">
      {/* Listings Section */}
      <div className="relative min-h-[calc(100vh-100px)]">
        {/* Category Title */}
        <h2 className="text-gray-400 text-lg font-medium px-4 mb-3">
          {formatCategoryName(category)}
        </h2>

        {/* Listing Masonry Grid */}
        <div className="movie-masonry">
          {listings.length > 0 ? (
            listings.map(listing => (
              <div key={listing.id} className="masonry-item">
                <ListingItem listing={listing} />
              </div>
            ))
          ) : (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center">
                <p className="text-gray-400 text-lg">
                  No listings available for {formatCategoryName(category)}
                </p>
                <p className="text-gray-500 text-sm mt-2">
                  Check back later or browse other categories
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Inject styles */}
      <style>
        {`
          .movie-masonry {
            column-count: 2;
            column-gap: 6px;
            padding: 0 4px;
            min-height: calc(100vh - 150px);
            position: relative;
          }

          @media (min-width: 640px) {
            .movie-masonry {
              column-count: 3;
            }
          }

          @media (min-width: 1024px) {
            .movie-masonry {
              column-count: 4;
            }
          }

          .masonry-item {
            break-inside: avoid;
            margin-bottom: 6px;
          }
        `}
      </style>
    </main>
  );
}